import React from 'react';
import { Eye, Check, AlertTriangle, Info, FileText } from 'lucide-react';

/**
 * Компонент с рекомендациями по доступности текста
 */
const AccessibilityGuidelines = ({ formData, results }) => {
  if (!results) return null;

  // Определяем уровень соответствия WCAG
  const getWCAGLevel = () => {
    const fontSize = parseFloat(results.fontSizes.px);
    const fontSizePoints = parseFloat(results.fontSizes.pt);
    const contrast = formData.colorScheme === 'dark' ? 'Высокий' : 'Средний';
    
    if (fontSize >= 18 && parseFloat(formData.lineHeight) >= 1.5) {
      return {
        level: 'AAA',
        description: 'Высший уровень доступности',
        icon: <Check className="w-5 h-5 text-green-500" />
      };
    } else if (fontSize >= 16 && parseFloat(formData.lineHeight) >= 1.4) {
      return {
        level: 'AA',
        description: 'Стандартный уровень доступности',
        icon: <Check className="w-5 h-5 text-emerald-500" />
      };
    } else if (fontSize >= 14) {
      return {
        level: 'A',
        description: 'Базовый уровень доступности',
        icon: <AlertTriangle className="w-5 h-5 text-amber-500" />
      };
    } else {
      return {
        level: 'Не соответствует',
        description: 'Не соответствует стандартам доступности',
        icon: <AlertTriangle className="w-5 h-5 text-red-500" />
      };
    }
  };

  // Проверка на соответствие различным требованиям
  const wcagLevel = getWCAGLevel();
  const fontSize = parseFloat(results.fontSizes.px);
  const lineHeight = parseFloat(formData.lineHeight);
  const letterSpacing = parseFloat(formData.letterSpacing);
  
  // Проверка на соответствие российским стандартам
  const russianStandardCompliance = {
    'GOST-P-57850': fontSize >= 18 && lineHeight >= 1.5,
    'SanPin': fontSize >= (formData.targetAudience === 'children' ? 14 : 12)
  };

  // Рекомендации по улучшению доступности
  const accessibilityImprovements = [];
  
  if (fontSize < 16) {
    accessibilityImprovements.push('Увеличьте размер шрифта до 16px или больше для основного текста.');
  }
  
  if (lineHeight < 1.5) {
    accessibilityImprovements.push('Увеличьте высоту строки (интерлиньяж) до 1.5 или больше для лучшей читаемости.');
  }
  
  if (letterSpacing < 0) {
    accessibilityImprovements.push('Избегайте отрицательного межбуквенного расстояния, это может затруднить чтение.');
  }
  
  if (formData.fontFamily.includes('serif') && formData.targetAudience === 'dyslexic') {
    accessibilityImprovements.push('Для людей с дислексией лучше использовать шрифты без засечек (sans-serif).');
  }
  
  if (formData.colorScheme === 'dark' && parseFloat(formData.fontWeight) < 500) {
    accessibilityImprovements.push('На темном фоне используйте более жирное начертание шрифта (500+) для лучшей читаемости.');
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-6 mb-8">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-5 flex items-center">
        <Eye className="w-5 h-5 mr-2 text-indigo-500" />
        Рекомендации по доступности текста
      </h3>

      {/* WCAG уровень соответствия */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-100 dark:border-indigo-800">
          <div className="flex items-start">
            {wcagLevel.icon}
            <div className="ml-3">
              <h4 className="font-medium text-gray-900 dark:text-white">
                WCAG {wcagLevel.level}
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {wcagLevel.description}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                Web Content Accessibility Guidelines — международный стандарт доступности веб-контента
              </p>
            </div>
          </div>
        </div>

        {/* Соответствие российским нормам */}
        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-100 dark:border-purple-800">
          <div className="flex items-start">
            <FileText className="w-5 h-5 text-purple-500" />
            <div className="ml-3">
              <h4 className="font-medium text-gray-900 dark:text-white">
                Российские стандарты
              </h4>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                <div className="flex items-center">
                  {russianStandardCompliance['GOST-P-57850'] 
                    ? <Check className="w-4 h-4 text-green-500 mr-1" /> 
                    : <AlertTriangle className="w-4 h-4 text-amber-500 mr-1" />
                  }
                  <span>ГОСТ Р 57850-2017 (сайты для слабовидящих)</span>
                </div>
                <div className="flex items-center mt-1">
                  {russianStandardCompliance['SanPin'] 
                    ? <Check className="w-4 h-4 text-green-500 mr-1" /> 
                    : <AlertTriangle className="w-4 h-4 text-amber-500 mr-1" />
                  }
                  <span>СанПиН 1.2.3685-21 (гигиенические нормативы)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Параметры текста для доступности */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-3">
          Параметры текста и доступность
        </h4>
        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Параметр
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Текущее значение
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Рекомендация
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Статус
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              <tr>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                  Размер шрифта
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                  {fontSize}px
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                  Минимум 16px, для людей с нарушениями зрения — 18-20px
                </td>
                <td className="px-4 py-3 text-sm">
                  {fontSize >= 16 
                    ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">Хорошо</span>
                    : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300">Улучшить</span>
                  }
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                  Межбуквенное расстояние
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                  {letterSpacing}em
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                  0 или положительное значение, 0.05-0.1em для людей с дислексией
                </td>
                <td className="px-4 py-3 text-sm">
                  {letterSpacing >= 0
                    ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">Хорошо</span>
                    : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300">Улучшить</span>
                  }
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                  Контрастность
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                  {formData.colorScheme === 'dark' ? 'Высокая' : 'Средняя'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                  Соотношение не менее 4.5:1, для крупного текста — 3:1
                </td>
                <td className="px-4 py-3 text-sm">
                  {formData.colorScheme === 'dark'
                    ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">Хорошо</span>
                    : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">Хорошо</span>
                  }
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                  Гарнитура шрифта
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white truncate max-w-[150px]">
                  {formData.fontFamily}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                  Sans-serif для экранов, serif или sans-serif для печати
                </td>
                <td className="px-4 py-3 text-sm">
                  {formData.fontFamily.includes('serif') && formData.mediaType === 'screen'
                    ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300">Улучшить</span>
                    : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">Хорошо</span>
                  }
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Рекомендации по улучшению доступности */}
      {accessibilityImprovements.length > 0 && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 mb-6">
          <div className="flex">
            <Info className="w-5 h-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">
                Рекомендации по улучшению доступности
              </h4>
              <ul className="space-y-1 text-sm text-blue-600 dark:text-blue-400 list-disc list-inside">
                {accessibilityImprovements.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Требования версии для слабовидящих в России */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-3">
          Требования версии для слабовидящих (ГОСТ Р 57850-2017)
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          В соответствии с законодательством РФ все государственные сайты и сайты организаций, оказывающих услуги населению,
          должны иметь версию для слабовидящих, соответствующую следующим требованиям:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-700 dark:text-gray-300 flex items-center mb-1">
              <Check className="w-4 h-4 text-green-500 mr-2" />
              Минимальный размер шрифта – 18px
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
              {fontSize >= 18 
                ? 'Ваш текст соответствует требованию'
                : 'Увеличьте размер шрифта текста'
              }
            </p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-700 dark:text-gray-300 flex items-center mb-1">
              <Check className="w-4 h-4 text-green-500 mr-2" />
              Минимальный интерлиньяж – 1.5
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
              {lineHeight >= 1.5
                ? 'Ваш текст соответствует требованию'
                : 'Увеличьте межстрочный интервал'
              }
            </p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-700 dark:text-gray-300 flex items-center mb-1">
              <Check className="w-4 h-4 text-green-500 mr-2" />
              Возможность увеличения шрифта до 200%
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
              {formData.includeAccessibility && parseFloat(formData.fontScale) >= 125
                ? 'Вы используете масштабирование шрифта'
                : 'Включите поддержку масштабирования'
              }
            </p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-700 dark:text-gray-300 flex items-center mb-1">
              <Check className="w-4 h-4 text-green-500 mr-2" />
              Высокая контрастность текста
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
              {formData.colorScheme === 'dark'
                ? 'Темный режим обеспечивает высокую контрастность'
                : 'Рекомендуется темный режим для максимальной контрастности'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Дополнительные рекомендации */}
      <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700">
        <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
          Дополнительные рекомендации
        </h4>
        <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>Избегайте длинных блоков текста, разбивайте их на параграфы по 3-4 предложения.</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>Используйте маркированные списки для удобства сканирования информации.</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>Выравнивайте текст по левому краю для лучшей читаемости (особенно для людей с дислексией).</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>Обеспечьте масштабируемость текста без потери содержимого и функциональности.</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>Для государственных и муниципальных сайтов обязательно наличие версии для слабовидящих по ГОСТ Р 52872-2019.</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default AccessibilityGuidelines;